import { useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ListNode, ListItemNode } from '@lexical/list';
import { HeadingNode } from '@lexical/rich-text';
import { LinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { 
  $getSelection, 
  $isRangeSelection, 
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $getRoot,
  $insertNodes
} from 'lexical';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon,
  List as ListIcon,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Image as ImageIcon,
  Smile
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useState } from 'react';
import EmojiPicker from 'emoji-picker-react';

// Toolbar Plugin
function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const formatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  };

  const formatItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  };

  const formatUnderline = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  };

  const formatBulletList = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        editor.dispatchCommand('INSERT_UNORDERED_LIST_COMMAND', undefined);
      }
    });
  };

  const formatNumberedList = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        editor.dispatchCommand('INSERT_ORDERED_LIST_COMMAND', undefined);
      }
    });
  };

  const formatAlignLeft = () => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
  };

  const formatAlignCenter = () => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
  };

  const formatAlignRight = () => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
  };

  const insertLink = () => {
    if (linkUrl.trim() === '') return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
    setLinkUrl('');
    setLinkDialogOpen(false);
  };

  const insertImage = () => {
    if (imageUrl.trim() === '') return;
    editor.update(() => {
      const root = $getRoot();
      const paragraph = root.getLastChild();
      if (paragraph) {
        const imgHtml = `<img src="${imageUrl}" alt="Image" style="max-width: 100%; height: auto;" />`;
        const parser = new DOMParser();
        const dom = parser.parseFromString(imgHtml, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        $insertNodes(nodes);
      }
    });
    setImageUrl('');
    setImageDialogOpen(false);
  };

  const insertEmoji = (emojiData) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.insertText(emojiData.emoji);
      }
    });
    setEmojiPickerOpen(false);
  };

  return (
    <div className="border-b bg-slate-50 p-2 flex flex-wrap gap-1">
      <Button
        size="sm"
        variant="ghost"
        onClick={formatBold}
        title="Gras (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </Button>
      
      <Button
        size="sm"
        variant="ghost"
        onClick={formatItalic}
        title="Italique (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </Button>
      
      <Button
        size="sm"
        variant="ghost"
        onClick={formatUnderline}
        title="Souligné (Ctrl+U)"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>

      <div className="w-px h-8 bg-slate-300 mx-1" />

      <Button
        size="sm"
        variant="ghost"
        onClick={formatBulletList}
        title="Liste à puces"
      >
        <ListIcon className="h-4 w-4" />
      </Button>

      <Button
        size="sm"
        variant="ghost"
        onClick={formatNumberedList}
        title="Liste numérotée"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>

      <div className="w-px h-8 bg-slate-300 mx-1" />

      <Button
        size="sm"
        variant="ghost"
        onClick={formatAlignLeft}
        title="Aligner à gauche"
      >
        <AlignLeft className="h-4 w-4" />
      </Button>

      <Button
        size="sm"
        variant="ghost"
        onClick={formatAlignCenter}
        title="Centrer"
      >
        <AlignCenter className="h-4 w-4" />
      </Button>

      <Button
        size="sm"
        variant="ghost"
        onClick={formatAlignRight}
        title="Aligner à droite"
      >
        <AlignRight className="h-4 w-4" />
      </Button>

      <div className="w-px h-8 bg-slate-300 mx-1" />

      <Button
        size="sm"
        variant="ghost"
        onClick={() => setLinkDialogOpen(true)}
        title="Insérer un lien"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>

      <Button
        size="sm"
        variant="ghost"
        onClick={() => setImageDialogOpen(true)}
        title="Insérer une image"
      >
        <ImageIcon className="h-4 w-4" />
      </Button>

      <Button
        size="sm"
        variant="ghost"
        onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
        title="Insérer un emoji"
      >
        <Smile className="h-4 w-4" />
      </Button>

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insérer un lien</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertLink()}
            />
            <Button onClick={insertLink} className="w-full">
              Insérer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insérer une image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertImage()}
            />
            <Button onClick={insertImage} className="w-full">
              Insérer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Emoji Picker */}
      {emojiPickerOpen && (
        <div className="absolute z-50 mt-2">
          <EmojiPicker onEmojiClick={insertEmoji} />
        </div>
      )}
    </div>
  );
}

// Plugin to load initial HTML content
function LoadContentPlugin({ htmlContent }) {
  const [editor] = useLexicalComposerContext();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (htmlContent && isFirstRender.current) {
      isFirstRender.current = false;
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlContent, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
    }
  }, [editor, htmlContent]);

  return null;
}

// Plugin to handle onChange and convert to HTML
function OnChangeHTMLPlugin({ onChange }) {
  const [editor] = useLexicalComposerContext();

  const handleChange = (editorState) => {
    editorState.read(() => {
      const htmlString = $generateHtmlFromNodes(editor);
      onChange(htmlString);
    });
  };

  return <OnChangePlugin onChange={handleChange} />;
}

// Main Lexical Editor Component
export default function LexicalEditor({ 
  value = '', 
  onChange, 
  placeholder = 'Écrivez votre message...' 
}) {
  const initialConfig = {
    namespace: 'EmailEditor',
    theme: {
      paragraph: 'mb-2',
      text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
      },
      list: {
        ul: 'list-disc list-inside ml-4',
        ol: 'list-decimal list-inside ml-4',
      },
    },
    onError: (error) => {
      console.error('Lexical Error:', error);
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      LinkNode,
    ],
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable 
                className="min-h-[300px] p-4 outline-none"
                style={{ direction: 'ltr', textAlign: 'left' }}
              />
            }
            placeholder={
              <div className="absolute top-4 left-4 text-slate-400 pointer-events-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <OnChangeHTMLPlugin onChange={onChange} />
        <LoadContentPlugin htmlContent={value} />
      </LexicalComposer>
    </div>
  );
}
